import React, { useEffect, useRef } from "react";
import CustomSlider from "@/widgets/customSlider/ui/CustomSlider/CustomSlider";
import { BillingPlanTeamsProps } from "@/shared/types/BillingPlanTeams";
import styles from "./BillingPlanTeams.module.scss";
import { useGetPlansQuery } from "@/features/auth/api/authAPI";
import Spinner from "../Spinner/Spinner";
import mixpanel from "@/shared/lib/mixpanel";
import { useMe, useCurrentOrganization } from "@/Hooks";

const BillingPlanTeams: React.FC<BillingPlanTeamsProps> = ({
  sliderValue,
  handleSliderChange,
  handleSelectPlan,
  pendingPlanData,
}) => {
  const { me } = useMe();
  const currentOrg = useCurrentOrganization();
  const previousSliderValueRef = useRef(sliderValue);

  useEffect(() => {
    if (sliderValue !== previousSliderValueRef.current) {
      // This condition ensures we only track an intentional change by the user via handleSliderChange,
      // not just any prop update if sliderValue could be controlled externally in other ways.
      // The actual tracking will happen in a new handler.
    }
    // Keep the ref in sync for the *next* comparison, regardless of how it changed.
    previousSliderValueRef.current = sliderValue;
  }, [sliderValue]);

  const { data: apiDataPlans } = useGetPlansQuery();

  if (!apiDataPlans || apiDataPlans.length === 0) {
    return (
      <div className={styles.root}>
        <Spinner />
      </div>
    );
  }

  const plan = apiDataPlans[0];
  const { unit_amount, id: priceId } = plan;

  // works as long azs recurring_interval is "month"

  const pricePerUser = unit_amount / 100 / plan.recurring_interval_count;
  const totalPrice = sliderValue * pricePerUser;

  const billedEvery =
    plan.recurring_interval === "month" && plan.recurring_interval_count === 1
      ? "Monthly"
      : `Every ${plan.recurring_interval_count} ${plan.recurring_interval}s`;

  const onSliderChangeWithTracking = (newSliderValue: number | number[]) => {
    const currentVal = Array.isArray(newSliderValue)
      ? newSliderValue[0]
      : newSliderValue;

    if (currentVal !== previousSliderValueRef.current) {
      // Only track if value actually changed
      const newTotalPrice = currentVal * pricePerUser;
      mixpanel.track("Team Size Selected", {
        selected_team_size: currentVal,
        previous_team_size: previousSliderValueRef.current,
        calculated_total_price: parseFloat(newTotalPrice.toFixed(2)),
        price_per_user: parseFloat(pricePerUser.toFixed(2)),
        plan_id: priceId, // Base plan ID
        plan_currency: plan.currency, // Assuming USD or get from plan data
        plan_interval: plan.recurring_interval,
        plan_interval_count: plan.recurring_interval_count,
        user_id: me?.id || null,
        user_email: me?.email || null,
        organization_id: currentOrg?.uuid || null,
      });
      // Call the original handler passed from parent
      handleSliderChange(newSliderValue);
      // Update ref *after* tracking and calling original handler,
      // so previousSliderValueRef had the correct "before" value for this event.
      // Note: The useEffect above will also update it, but this ensures it's updated
      // immediately after this specific interaction if there's any delay in prop updates.
      // previousSliderValueRef.current = currentVal; // This is now handled by the useEffect on sliderValue prop
    } else {
      // If value didn't change (e.g. slider snapped back), still call original handler
      handleSliderChange(newSliderValue);
    }
  };
  
  return (
    <div className={styles.root}>
      <div className={styles.planTeamsSlider}>
        <span className={styles.planTeamsMin}>2</span>
        <CustomSlider
          min={2}
          max={20}
          step={1}
          value={sliderValue}
          onChange={onSliderChangeWithTracking}
        />
        <span className={styles.planTeamsMax}>20</span>
      </div>
      <div className={styles.planTeamsPrice}>
        <p>
          Full access to all app designs, advanced search, and additional
          features.
        </p>
        <h1 className={styles.planTeamsPrice__price}>
          ${totalPrice.toFixed(2)}/<span>per month</span>
        </h1>
        <span className={styles.planTeamsBilled}>Billed {billedEvery}</span>
      </div>
      <div className={styles.planTeamsButton}>
        <button onClick={() => handleSelectPlan(priceId, sliderValue)}>
          {" "}
          {pendingPlanData ? "Processing..." : "Get Team Plan"}
        </button>
      </div>
      <div className={styles.footerText}>
        <a href="mailto:hi@screensdesign.com">
          Contact us if you need a bigger plan
        </a>
      </div>
    </div>
  );
};

export default BillingPlanTeams;
